// Resolves an extracted position against the Softline article masterdata.
// Pure over a lookup port so the strategy is testable without HTTP and the
// service stays a thin signal/concurrency shell.
import type { MasterdataProduct } from '@softapps/core/masterdata';
import { PositionMatch } from '../types/product-match';

/** Data port of {@link matchDraftFields} — implemented by ProductMatchService. */
export interface ProductLookup {
  /** Products whose article number equals `key` (structural filter, not a search). */
  byKey(key: string): Promise<MasterdataProduct[]>;
  /** Free-text search over the article masterdata. */
  search(term: string, limit: number): Promise<MasterdataProduct[]>;
}

/** The draft fields a match can be derived from. */
export interface MatchableFields {
  product_number?: string;
  ean?: string;
  description?: string;
}

/** What the DOCUMENT says about itself — not part of a single position. */
export interface MatchContext {
  /**
   * Supplier the document came from. A supplier article number is unique only
   * WITHIN one supplier, so without it a number hit can only be a suggestion.
   */
  supplierName?: string | null;
}

const SUPPLIER_SEARCH_LIMIT = 10;
const EAN_SEARCH_LIMIT = 10;
const NAME_SEARCH_LIMIT = 5;
/** Below this an "EAN" is a stray number, not a barcode. */
const MIN_EAN_DIGITS = 8;
const MIN_NAME_CHARS = 3;
/** Shorter supplier names are compared verbatim, longer ones by containment. */
const MIN_SUPPLIER_CONTAINS_CHARS = 4;

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function digits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/** Supplier names for comparison: umlauts folded, letters and digits only. */
export function foldName(value: string | null | undefined): string {
  return normalize(value)
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Legal-form suffixes differ between document and masterdata ("Muster GmbH"
 * vs. "Muster GmbH & Co KG"), so containment counts — but only for names long
 * enough that it cannot happen by accident. Both sides folded (`foldName`).
 */
export function sameSupplier(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a.length < MIN_SUPPLIER_CONTAINS_CHARS || b.length < MIN_SUPPLIER_CONTAINS_CHARS)
    return a === b;
  return a.includes(b) || b.includes(a);
}

/**
 * Same rule as masterdata's `findDirectKeyMatch`, inlined: importing it would
 * pull the whole `@softapps/core/masterdata` barrel into this pure module (and
 * the barrel cannot be evaluated under vitest — circular legacy store init).
 */
function uniqueKeyMatch(
  candidates: readonly MasterdataProduct[],
  key: string
): MasterdataProduct | null {
  const needle = normalize(key);
  const hits = candidates.filter((p) => normalize(p.key) === needle);
  return hits.length === 1 ? hits[0] : null;
}

/** One hit is a match; several stay a suggestion carrying every candidate. */
function verdict(
  candidates: readonly MasterdataProduct[],
  via: 'supplier_number' | 'ean'
): PositionMatch | null {
  if (!candidates.length) return null;
  if (candidates.length === 1) return { status: 'matched', product: candidates[0], via };
  return {
    status: 'suggested',
    product: candidates[0],
    via,
    candidates: [...candidates],
  };
}

/**
 * A hit found by another identifier must not silently outrank a contradicting
 * EAN on the row — it degrades to a suggestion instead of claiming a match. A
 * candidate without an EAN in the masterdata contradicts nothing.
 */
function vetoByEan(match: PositionMatch, rowEan: string): PositionMatch {
  if (match.status !== 'matched' || !rowEan) return match;
  const candidateEan = digits(match.product.ean);
  if (!candidateEan || candidateEan === rowEan) return match;
  return {
    status: 'suggested',
    product: match.product,
    via: match.via,
    candidates: [match.product],
  };
}

/** Every product whose supplier list carries `number` — under any supplier. */
function bySupplierNumber(
  candidates: readonly MasterdataProduct[],
  number: string
): MasterdataProduct[] {
  return candidates.filter((p) =>
    (p.suppliers ?? []).some((s) => normalize(s.productNumber) === number)
  );
}

/** Of those, the ones carrying it under the supplier the document came from. */
function ofDocumentSupplier(
  candidates: readonly MasterdataProduct[],
  number: string,
  supplier: string
): MasterdataProduct[] {
  if (!supplier) return [];
  return candidates.filter((p) =>
    (p.suppliers ?? []).some(
      (s) =>
        normalize(s.productNumber) === number && sameSupplier(foldName(s.name), supplier)
    )
  );
}

/**
 * Cascade, most to least trustworthy: EAN → supplier article number → own
 * article number → description. The EAN identifies an article globally, and
 * the number printed on a supplier document is the SUPPLIER's article number —
 * so it is matched against the masterdata supplier numbers before our own keys,
 * where it could collide with an unrelated article.
 *
 * A hit only becomes `matched` when it is unambiguous AND nothing on the row
 * contradicts it: a differing EAN, or a supplier number carried by a supplier
 * other than the document's, leaves a `suggested` (with its candidates) for the
 * user to decide on. Blank fields skip their step without costing a request.
 */
export async function matchDraftFields(
  fields: MatchableFields,
  lookup: ProductLookup,
  context: MatchContext = {}
): Promise<PositionMatch> {
  const ean = digits(fields.ean);
  const rowEan = ean.length >= MIN_EAN_DIGITS ? ean : '';
  if (rowEan) {
    const byEan = (await lookup.search(rowEan, EAN_SEARCH_LIMIT)).filter(
      (p) => digits(p.ean) === rowEan
    );
    const eanVerdict = verdict(byEan, 'ean');
    if (eanVerdict) return eanVerdict;
  }

  const productNumber = (fields.product_number ?? '').trim();
  if (productNumber) {
    const number = normalize(productNumber);
    const supplier = foldName(context.supplierName);
    const candidates = bySupplierNumber(
      await lookup.search(productNumber, SUPPLIER_SEARCH_LIMIT),
      number
    );
    if (candidates.length) {
      const own = ofDocumentSupplier(candidates, number, supplier);
      const supplierVerdict: PositionMatch = own.length
        ? (verdict(own, 'supplier_number') as PositionMatch)
        : {
            status: 'suggested',
            product: candidates[0],
            via: 'supplier_number',
            candidates,
          };
      return vetoByEan(supplierVerdict, rowEan);
    }

    const direct = uniqueKeyMatch(await lookup.byKey(productNumber), productNumber);
    if (direct)
      return vetoByEan({ status: 'matched', product: direct, via: 'key' }, rowEan);
  }

  const description = (fields.description ?? '').trim();
  if (description.length >= MIN_NAME_CHARS) {
    const hits = await lookup.search(description, NAME_SEARCH_LIMIT);
    // Never `matched`: the search ranks by relevance, not by identity.
    if (hits.length)
      return { status: 'suggested', product: hits[0], via: 'name', candidates: hits };
  }

  return { status: 'none' };
}
